import { DefaultValueConfig, featureFlipping, Locale } from '@headless-workspace/config';
import { PropsWithDataLayer } from '@headless-workspace/glow-ds';
import { ObjectHelpers, PredicateHelpers, UrlHelpers } from '@headless-workspace/utils';
import { mapURLToImage, ProductType } from '../../../client';
import { VipBrandsValue } from '../config';
import { TileProductDTO, VariantDTO } from './TileProductDTO';
import { BaseProductValue, mapProductBrandData, mapProductPriceData } from './index';

type ProductTag = {
    id: string;
    label: string;
};

export type TileProductValue = Readonly<
    BaseProductValue &
        PropsWithDataLayer & {
            minPrice?: number;
            totalIndividualPrice?: number;
            pricePerUnit?: string;

            reviewsCount?: number;
            rating?: number;

            isMasterProduct: boolean;
            defaultVariantId: string;
            variantName?: string;
            variantsCount?: number;
            isAvailable?: boolean;

            isEngravable: boolean;
            sku: string;
            type: ProductType;
            tags?: ProductTag[];
            originalPriceText?: string;
        }
>;

const findDefaultVariant = (variants: VariantDTO[]): VariantDTO | undefined => {
    return variants.find(({ isDefault }) => isDefault);
};

export const mapTileProduct = (
    dto: TileProductDTO,
    vipBrands: VipBrandsValue,
    originalPriceText?: string,
): TileProductValue => {
    const [name, ...description] = dto.name.split(' - ');

    const defaultVariant = dto.isMaster ? findDefaultVariant(dto.variants) : undefined;

    const defaultImage = ObjectHelpers.createImageWithSrc(DefaultValueConfig.imagePlaceholder);
    const imageAndVariantName =
        defaultVariant?.imageURL && defaultVariant?.description
            ? {
                  image: mapURLToImage(defaultVariant.imageURL) ?? defaultImage,
                  variantName: defaultVariant.description,
              }
            : {
                  image: mapURLToImage(dto.imageUrl) ?? defaultImage,
                  variantName: dto.description,
              };

    return {
        id: dto.id,
        name,
        description: description.join(' - '),
        href: featureFlipping.isPathnameOnlyEnabled
            ? UrlHelpers.getHrefPathname(dto.url ?? DefaultValueConfig.href)
            : (dto.url ?? DefaultValueConfig.href),
        isEngravable: dto.isEngravable,
        minPrice: dto.minPrice,
        pricePerUnit: dto.pricePerUnit,
        totalIndividualPrice: dto.valuePrice,
        originalPriceText: dto.formerPrice && !!dto.price ? originalPriceText : undefined,
        reviewsCount: dto.reviewsCount,
        rating: dto.rating,
        isMasterProduct: dto.isMaster,
        defaultVariantId: dto.defaultVariantId ?? '',
        sku: dto.defaultVariantId ?? dto.id,
        variantsCount: dto.variantsCount,
        type: ProductType[dto.type],
        tags: dto.flags
            ?.map(({ id, label }) => (label ? { id, label } : null))
            .filter(PredicateHelpers.notNullOrUndefined),
        ...mapProductBrandData(dto, vipBrands),
        ...mapProductPriceData(dto, dto.currency),
        ...imageAndVariantName,
    };
};
